/**
* NotificationService
* 
* A comprehensive service for handling notifications across the Gigify platform.
* Provides functionality to:
* - Send notifications to users via multiple channels (email, in-app)
* - Manage notification preferences and delivery settings
* - Track notification history and user interaction
* - Handle notification categories for different aspects of the platform
* - Provide notification templates for consistent messaging
* - Route notifications to appropriate recipients (Talent or Organiser)
*/

import { db } from '../Firebase/FirebaseIndex';
import { 
 collection, 
 addDoc, 
 getDocs, 
 query, 
 where, 
 orderBy, 
 limit,
 updateDoc,
 doc,
 deleteDoc,
 serverTimestamp,
 Timestamp,
 getDoc,
 startAfter,
 writeBatch
} from 'firebase/firestore';
import emailService from './EmailService';
import { formatDate, parseFirebaseDate } from '../../utils/DateUtils';
import debug from '../../utils/debug';

// Cache for frequently accessed data
const cache = {
  users: new Map(),
  templates: new Map(),
  // Cache expiration in milliseconds (5 minutes)
  expiration: 5 * 60 * 1000,
  // Store timestamps of when items were cached
  timestamps: new Map(),
  
  // Get item from cache with expiration check
  get(key, collection) {
    const map = this[collection];
    if (!map) return null;
    
    const timestamp = this.timestamps.get(`${collection}:${key}`);
    if (!timestamp) return null;
    
    // Check if cache entry has expired
    if (Date.now() - timestamp > this.expiration) {
      map.delete(key);
      this.timestamps.delete(`${collection}:${key}`);
      return null;
    }
    
    return map.get(key);
  },
  
  // Set item in cache with timestamp
  set(key, value, collection) {
    const map = this[collection];
    if (!map) return;
    
    map.set(key, value);
    this.timestamps.set(`${collection}:${key}`, Date.now());
  },
  
  // Clear specific item from cache
  clear(key, collection) {
    const map = this[collection];
    if (!map) return;
    
    map.delete(key);
    this.timestamps.delete(`${collection}:${key}`);
  },
  
  // Clear entire cache or specific collection
  clearAll(collection = null) {
    if (collection) {
      if (this[collection]) {
        this[collection].clear();
        // Clear related timestamps
        for (const key of this.timestamps.keys()) {
          if (key.startsWith(`${collection}:`)) {
            this.timestamps.delete(key);
          }
        }
      }
    } else {
      // Clear all collections
      this.users.clear();
      this.templates.clear();
      this.timestamps.clear();
    }
  }
};

class NotificationService {
 constructor() {
   // Notification categories
   this.CATEGORIES = {
     BOOKING: 'booking',
     PAYMENT: 'payment',
     REMINDER: 'reminder',
     PROFILE: 'profile',
     SYSTEM: 'system',
     AGREEMENT: 'agreement',
     DISPUTE: 'dispute',
     REVIEW: 'review',
     MESSAGE: 'message',
     EVENT: 'event'
   };
   
   // Notification priorities
   this.PRIORITIES = {
     LOW: 'low',
     MEDIUM: 'medium',
     HIGH: 'high',
     URGENT: 'urgent'
   };
   
   // Notification channels
   this.CHANNELS = {
     EMAIL: 'email',
     IN_APP: 'in_app',
     BOTH: 'both'
   };
   
   // Notification status
   this.STATUS = {
     SENT: 'sent',
     DELIVERED: 'delivered',
     READ: 'read',
     FAILED: 'failed',
     EXPIRED: 'expired'
   };
   
   // User types for routing
   this.USER_TYPES = {
     TALENT: 'Talent',
     ORGANISER: 'Organiser'
   };
   
   // Booking status constants for consistency with Database Bible
   this.BOOKING_STATUS = {
     PENDING_REQUEST: 'pending_request',
     REQUEST_APPROVED: 'request_approved',
     REQUEST_DECLINED: 'request_declined',
     AGREEMENT_GENERATED: 'agreement_generated',
     AGREEMENT_SENT: 'agreement_sent',
     AGREEMENT_SIGNED: 'agreement_signed',
     PAYMENT_PENDING: 'payment_pending',
     PAYMENT_COMPLETE: 'payment_complete',
     EVENT_UPCOMING: 'event_upcoming',
     talent_performance_complete: 'talent_performance_complete',
     REVIEW_PENDING: 'review_pending',
     DISPUTED: 'disputed'
   };
   
   // Rate limiting state
   this.rateLimits = {
     // Store timestamps of last notification for each user
     userTimestamps: new Map(),
     // Minimum time between notifications for a user (in milliseconds)
     minTimeBetweenNotifications: 500 // 500ms minimum between notifications
   };
 }

 /**
  * Send an email notification using the EmailService
  * @param {string} email - Recipient email address
  * @param {string} subject - Email subject
  * @param {string} body - Email body
  * @param {Object} data - Additional data for the email
  * @returns {Promise<Object>} Result of the email sending
  */
 async sendEmailNotification(email, subject, body, data = {}) {
   return emailService.sendEmailNotification(email, subject, body, data);
 }

 /**
  * Format a date in a human-readable way
  * @param {Date|Timestamp|String} date - Date to format
  * @returns {String} Formatted date string
  */
 formatDate(date) {
   return formatDate(date);
 }

 /**
  * Apply rate limiting to notifications for a user
  * @param {string} userId - User ID
  * @returns {Promise<void>} Resolves when rate limit check is complete
  */
 async applyRateLimit(userId) {
   const lastTime = this.rateLimits.userTimestamps.get(userId);
   const now = Date.now();
   
   if (lastTime && (now - lastTime) < this.rateLimits.minTimeBetweenNotifications) {
     // Wait for the minimum time to pass
     const waitTime = this.rateLimits.minTimeBetweenNotifications - (now - lastTime);
     await new Promise(resolve => setTimeout(resolve, waitTime));
   }
   
   // Update timestamp
   this.rateLimits.userTimestamps.set(userId, Date.now());
 }

 /**
  * Send a notification to a user through one or more channels
  * @param {string} userId - User ID of the recipient
  * @param {string} title - Notification title
  * @param {string} body - Notification body
  * @param {Object} options - Additional notification options
  * @returns {Promise<Object>} Result of the notification sending
  */
 async sendNotification(userId, title, body, options = {}) {
   try {
     // Apply rate limiting
     await this.applyRateLimit(userId);
     
     // Set default options
     const defaultOptions = {
       category: this.CATEGORIES.SYSTEM,
       priority: this.PRIORITIES.MEDIUM,
       channel: this.CHANNELS.BOTH,
       data: {},
       bookingId: null,
       route: null,
       email: null, // Optional override for user's email
       template: null,
       scheduledFor: null,
       actions: [],
       userType: null // Record the target user type for filtering
     };
     
     const notificationOptions = { ...defaultOptions, ...options };
     const { 
       category, 
       priority, 
       channel, 
       data, 
       bookingId, 
       route, 
       email, 
       template,
       userType
     } = notificationOptions;
     
     // Get user's email if not provided and if email notifications are enabled
     let userEmail = email;
     if ((channel === this.CHANNELS.EMAIL || channel === this.CHANNELS.BOTH) && !userEmail) {
       const userDoc = await this.getUserData(userId);
       if (userDoc) {
         userEmail = userDoc.profile?.email;
       }
     }
     
     // Create notification record in Firestore
     const notificationData = {
       userId,
       title,
       body,
       category,
       priority,
       isRead: false,
       createdAt: serverTimestamp(),
       scheduledFor: notificationOptions.scheduledFor ? Timestamp.fromDate(notificationOptions.scheduledFor) : null,
       data
     };
     
     if (bookingId) {
       notificationData.bookingId = bookingId;
     }
     
     if (route) {
       notificationData.route = route;
     }
     
     if (userType) {
       notificationData.userType = userType;
     }
     
     if (notificationOptions.actions && notificationOptions.actions.length > 0) {
       notificationData.actions = notificationOptions.actions;
     }
     
     // Store notification in Firestore
     const notificationRef = await addDoc(collection(db, 'notifications'), notificationData);
     debug.log('NotificationService', 'Created notification record', { notificationId: notificationRef.id });
     
     // Send notification through selected channels
     const channelResults = {};
     
     // Send in-app notification - no additional action needed since we're already storing
     // in the main notifications collection
     if (channel === this.CHANNELS.IN_APP || channel === this.CHANNELS.BOTH) {
       channelResults.inApp = { success: true };
     }
     
     // Send email notification
     if ((channel === this.CHANNELS.EMAIL || channel === this.CHANNELS.BOTH) && userEmail) {
       try {
         await this.sendEmailNotification(userEmail, title, body, {
           template,
           type: category,
           bookingId,
           userType,
           ...data
         });
         channelResults.email = { success: true };
       } catch (error) {
         debug.error('NotificationService', 'Error sending email notification', error);
         channelResults.email = { success: false, error: error.message };
       }
     }
     
     // Update notification record with delivery status
     const overallSuccess = 
       (channel === this.CHANNELS.BOTH && (channelResults.inApp?.success || channelResults.email?.success)) ||
       (channel === this.CHANNELS.IN_APP && channelResults.inApp?.success) ||
       (channel === this.CHANNELS.EMAIL && channelResults.email?.success);
     
     await updateDoc(doc(db, 'notifications', notificationRef.id), {
       status: overallSuccess ? this.STATUS.DELIVERED : this.STATUS.FAILED,
       channelResults,
       updatedAt: serverTimestamp()
     });
     
     return {
       success: overallSuccess,
       notificationId: notificationRef.id,
       channelResults
     };
   } catch (error) {
     debug.error('NotificationService', 'Error sending notification', error);
     return { success: false, error: error.message };
   }
 }

 /**
  * Send a booking-related notification with standardized formatting
  * @param {string} recipientId - User ID of the recipient
  * @param {string} recipientType - Type of recipient (Talent or Organiser)
  * @param {string} type - Type of booking notification
  * @param {string} bookingId - ID of the booking
  * @param {Object} data - Additional data for the notification
  * @returns {Promise<Object>} Result of the notification sending
  */
 async sendBookingNotification(recipientId, recipientType, type, bookingId, data = {}) {
   try {
     // Validate recipient type
     if (!Object.values(this.USER_TYPES).includes(recipientType)) {
       throw new Error(`Invalid recipient type: ${recipientType}. Must be either 'Talent' or 'Organiser'`);
     }
     
     // Format any date information in the data
     const formattedData = { ...data };
     if (data.date) {
       formattedData.formattedDate = formatDate(data.date);
     }
     
     // Get notification template based on type
     const template = this.getBookingNotificationTemplate(type, formattedData);
     
     if (!template) {
       throw new Error(`Unknown booking notification type: ${type}`);
     }
     
     // Check if this notification type is applicable for this recipient type
     if (template.forRecipient && template.forRecipient !== recipientType) {
       debug.log('NotificationService', `Skipping notification ${type} as it's not intended for ${recipientType}`);
       return { success: true, skipped: true, reason: `Notification type not applicable for ${recipientType}` };
     }
     
     // Set appropriate route to booking management with bookingId query parameter
     const route = `/booking-management/${recipientId}?bookingId=${bookingId}`;
     
     // Send notification
     return await this.sendNotification(
       recipientId,
       template.subject,
       template.body,
       {
         category: this.CATEGORIES.BOOKING,
         priority: template.priority || this.PRIORITIES.MEDIUM,
         channel: template.channel || this.CHANNELS.BOTH,
         bookingId,
         route,
         template: type,
         userType: recipientType,
         data: formattedData,
         actions: template.actions || []
       }
     );
   } catch (error) {
     debug.error('NotificationService', 'Error sending booking notification', error);
     return { success: false, error: error.message };
   }
 }

 /**
  * Send notifications for a new booking request to both talent and organiser
  * @param {Object} bookingData - The booking data
  * @returns {Promise<Object>} Results of the notifications
  */
 async sendBookingRequestNotifications(bookingData) {
   try {
     const results = {
       talent: null,
       organiser: null
     };
     
     // Format date for display
     const eventDate = bookingData.event?.date;
     const formattedDate = eventDate ? formatDate(eventDate) : 'the specified date';
     
     // Notification data
     const notificationData = {
       eventName: bookingData.event?.eventName || 'an event',
       date: eventDate, // Keep original date object for further processing
       formattedDate: formattedDate, // Include formatted date
       venue: bookingData.event?.venue || 'the venue'
     };
     
     // Send notification to talent about receiving a request
     if (bookingData.talentUID) {
       results.talent = await this.sendBookingNotification(
         bookingData.talentUID,
         this.USER_TYPES.TALENT,
         'booking_request_received',
         bookingData.id,
         notificationData
       );
     }
     
     // Send confirmation to organiser that request was sent
     if (bookingData.organiserUID) {
       results.organiser = await this.sendBookingNotification(
         bookingData.organiserUID,
         this.USER_TYPES.ORGANISER,
         'booking_request_sent',
         bookingData.id,
         notificationData
       );
     }
     
     return {
       success: results.talent?.success || results.organiser?.success,
       results
     };
   } catch (error) {
     debug.error('NotificationService', 'Error sending booking request notifications', error);
     return { success: false, error: error.message };
   }
 }

 /**
  * Send a payment-related notification with standardized formatting
  * @param {string} recipientId - User ID of the recipient
  * @param {string} recipientType - Type of recipient (Talent or Organiser)
  * @param {string} type - Type of payment notification
  * @param {string} bookingId - ID of the booking
  * @param {Object} data - Additional data for the notification
  * @returns {Promise<Object>} Result of the notification sending
  */
 async sendPaymentNotification(recipientId, recipientType, type, bookingId, data = {}) {
   try {
     // Validate recipient type
     if (!Object.values(this.USER_TYPES).includes(recipientType)) {
       throw new Error(`Invalid recipient type: ${recipientType}. Must be either 'Talent' or 'Organiser'`);
     }
     
     // Format any date information in the data
     const formattedData = { ...data };
     if (data.date) {
       formattedData.formattedDate = formatDate(data.date);
     }
     if (data.eventDate) {
       formattedData.formattedDate = formatDate(data.eventDate);
     }
     
     // Get notification template based on type
     const template = this.getPaymentNotificationTemplate(type, formattedData);
     
     if (!template) {
       throw new Error(`Unknown payment notification type: ${type}`);
     }
     
     // Check if this notification type is applicable for this recipient type
     if (template.forRecipient && template.forRecipient !== recipientType) {
       debug.log('NotificationService', `Skipping notification ${type} as it's not intended for ${recipientType}`);
       return { success: true, skipped: true, reason: `Notification type not applicable for ${recipientType}` };
     }
     
     // Set appropriate route to booking management with bookingId query parameter
     const route = `/booking-management/${recipientId}?bookingId=${bookingId}`;
     
     // Send notification
     return await this.sendNotification(
       recipientId,
       template.subject,
       template.body,
       {
         category: this.CATEGORIES.PAYMENT,
         priority: template.priority || this.PRIORITIES.HIGH,
         channel: template.channel || this.CHANNELS.BOTH,
         bookingId,
         route,
         template: type,
         userType: recipientType,
         data: formattedData,
         actions: template.actions || []
       }
     );
   } catch (error) {
     debug.error('NotificationService', 'Error sending payment notification', error);
     return { success: false, error: error.message };
   }
 }

 /**
  * Send an agreement-related notification with standardized formatting
  * @param {string} recipientId - User ID of the recipient
  * @param {string} recipientType - Type of recipient (Talent or Organiser)
  * @param {string} type - Type of agreement notification
  * @param {string} bookingId - ID of the booking
  * @param {Object} data - Additional data for the notification
  * @returns {Promise<Object>} Result of the notification sending
  */
 async sendAgreementNotification(recipientId, recipientType, type, bookingId, data = {}) {
   try {
     // Validate recipient type
     if (!Object.values(this.USER_TYPES).includes(recipientType)) {
       throw new Error(`Invalid recipient type: ${recipientType}. Must be either 'Talent' or 'Organiser'`);
     }
     
     // Format any date information in the data
     const formattedData = { ...data };
     if (data.eventDate) {
       formattedData.formattedDate = formatDate(data.eventDate);
     }
     
     // Get notification template based on type
     const template = this.getAgreementNotificationTemplate(type, formattedData);
     
     if (!template) {
       throw new Error(`Unknown agreement notification type: ${type}`);
     }
     
     // Check if this notification type is applicable for this recipient type
     if (template.forRecipient && template.forRecipient !== recipientType) {
       debug.log('NotificationService', `Skipping notification ${type} as it's not intended for ${recipientType}`);
       return { success: true, skipped: true, reason: `Notification type not applicable for ${recipientType}` };
     }
     
     // Set appropriate route to booking management with bookingId query parameter
     const route = `/booking-management/${recipientId}?bookingId=${bookingId}`;
     
     // Add actions to signing notifications
     let actions = [];
     if (type === 'agreement_ready' || type === 'agreement_reminder') {
       actions = [
         {
           label: 'Sign Agreement',
           url: route,
           primary: true
         }
       ];
     } else if (type === 'agreement_signed' && recipientType === this.USER_TYPES.ORGANISER) {
       actions = [
         {
           label: 'Make Payment',
           url: route,
           primary: true
         }
       ];
     }
     
     // Add bookingId to formattedData for template use
     formattedData.bookingId = bookingId;
     
     // Send notification
     return await this.sendNotification(
       recipientId,
       template.subject,
       template.body,
       {
         category: this.CATEGORIES.AGREEMENT,
         priority: template.priority || this.PRIORITIES.HIGH,
         channel: template.channel || this.CHANNELS.BOTH,
         bookingId,
         route,
         template: type,
         userType: recipientType,
         data: formattedData,
         actions: actions.length > 0 ? actions : (template.actions || [])
       }
     );
   } catch (error) {
     debug.error('NotificationService', 'Error sending agreement notification', error);
     return { success: false, error: error.message };
   }
 }

 /**
 * Send notifications for TradeSafe webhook events
 * @param {string} webhookType - Type of webhook event
 * @param {string} bookingId - ID of the booking
 * @param {Object} data - Additional data for the notifications
 * @returns {Promise<Object>} Results of sending notifications
 */
async sendTradesafeWebhookNotification(webhookType, bookingId, data = {}) {
  try {
    debug.firebase('NotificationService', 'Sending TradeSafe webhook notifications', { 
      webhookType, 
      bookingId 
    });
    
    // Get booking data if needed and not provided
    let bookingData;
    if (!data.talentId || !data.organiserId) {
      const bookingRef = doc(db, 'Bookings', bookingId);
      const bookingSnap = await getDoc(bookingRef);
      
      if (!bookingSnap.exists()) {
        throw new Error(`Booking not found: ${bookingId}`);
      }
      
      bookingData = bookingSnap.data();
      data.talentId = bookingData.talentUID;
      data.organiserId = bookingData.organiserUID;
    }
    
    const results = {
      talent: null,
      organiser: null
    };
    
    // Prepare base notification data
    const notificationData = {
      ...data,
      eventName: data.eventName || 'Event',
      bookingId
    };
    
    // Determine notification types based on webhook type
    switch (webhookType) {
      case 'transport_payment_secured':
        // Notify talent about logistics payment in escrow
        results.talent = await this.sendPaymentNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'payment_in_escrow',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0,
            paymentType: 'logistics'
          }
        );
        
        // Notify organiser about logistics payment complete
        results.organiser = await this.sendPaymentNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'payment_complete',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0,
            paymentType: 'logistics'
          }
        );
        break;
        
      case 'payment_in_escrow':
        // Notify talent about payment in escrow
        results.talent = await this.sendPaymentNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'payment_in_escrow',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0
          }
        );
        
        // Notify organiser about payment complete
        results.organiser = await this.sendPaymentNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'payment_complete',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0
          }
        );
        break;
        
      case 'logistics_payment_released':
        // Notify talent about logistics payment released
        results.talent = await this.sendPaymentNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'logistics_payment_released',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0
          }
        );
        
        // Notify organiser about logistics payment released
        results.organiser = await this.sendPaymentNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'transport_payment_released',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0
          }
        );
        break;
        
      case 'performance_completed':
        // Notify organiser about performance completion
        results.organiser = await this.sendBookingNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'performance_completed',
          bookingId,
          notificationData
        );
        break;
        
      case 'performance_accepted':
        // Notify talent performance was accepted
        results.talent = await this.sendBookingNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'performance_accepted',
          bookingId,
          notificationData
        );
        
        // Notify organiser about acceptance confirmation
        results.organiser = await this.sendBookingNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'performance_acceptance_confirmed',
          bookingId,
          notificationData
        );
        break;
        
      case 'booking_disputed':
        // Notify both parties about dispute
        results.talent = await this.sendBookingNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'booking_disputed',
          bookingId,
          notificationData
        );
        
        results.organiser = await this.sendBookingNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'booking_disputed',
          bookingId,
          notificationData
        );
        break;
        
      case 'transport_payment_refunded':
      case 'transport_payment_cancelled':
        // Notify both parties about logistics payment refund/cancellation
        results.talent = await this.sendBookingNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          webhookType,
          bookingId,
          notificationData
        );
        
        results.organiser = await this.sendBookingNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          webhookType,
          bookingId,
          notificationData
        );
        break;
        
      case 'payment_refunded':
        // Notify both parties about payment refund
        results.talent = await this.sendPaymentNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'payment_refunded',
          bookingId,
          notificationData
        );
        
        results.organiser = await this.sendPaymentNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'payment_refunded',
          bookingId,
          notificationData
        );
        break;
        
      case 'booking_cancelled':
        // Notify both parties about cancellation
        results.talent = await this.sendBookingNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'booking_cancelled',
          bookingId,
          notificationData
        );
        
        results.organiser = await this.sendBookingNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'booking_cancelled',
          bookingId,
          notificationData
        );
        break;
        
      case 'payment_released':
        // Notify talent about payment release
        results.talent = await this.sendPaymentNotification(
          data.talentId,
          this.USER_TYPES.TALENT,
          'payment_released',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0
          }
        );
        
        // Notify organiser about release completion
        results.organiser = await this.sendPaymentNotification(
          data.organiserId,
          this.USER_TYPES.ORGANISER,
          'payment_released_notification',
          bookingId,
          {
            ...notificationData,
            amount: data.amount || 0
          }
        );
        break;
        
      default:
        debug.warn('NotificationService', `Unknown TradeSafe webhook type: ${webhookType}`);
        return { 
          success: false, 
          error: `Unknown webhook type: ${webhookType}`
        };
    }
    
    debug.firebase('NotificationService', 'TradeSafe webhook notifications sent', { 
      webhookType, 
      talentSuccess: results.talent?.success, 
      organiserSuccess: results.organiser?.success 
    });
    
    return {
      success: results.talent?.success || results.organiser?.success,
      results
    };
  } catch (error) {
    debug.error('NotificationService', 'Error sending TradeSafe webhook notifications', error);
    return { success: false, error: error.message };
  }
}
 
 /**
  * Send a notification to both talent and organiser for a booking
  * @param {Object} bookingData - The booking data including talent and organiser IDs
  * @param {string} talentSubject - Subject line for talent notification
  * @param {string} talentBody - Message body for talent notification
  * @param {string} organiserSubject - Subject line for organiser notification
  * @param {string} organiserBody - Message body for organiser notification
  * @param {Object} options - Additional notification options
  * @returns {Promise<Object>} Results of both notifications
  */
 async sendBookingParticipantsNotification(bookingData, talentSubject, talentBody, organiserSubject, organiserBody, options = {}) {
   try {
     const defaultOptions = {
       category: this.CATEGORIES.BOOKING,
       priority: this.PRIORITIES.MEDIUM,
       channel: this.CHANNELS.BOTH,
       data: {},
       template: null
     };
     
     const notificationOptions = { ...defaultOptions, ...options };
     
     // Format date information if present
     let eventDate = null;
     let formattedDate = null;
     
     if (bookingData.event?.date) {
       eventDate = bookingData.event.date;
       formattedDate = formatDate(eventDate);
       notificationOptions.data.formattedDate = formattedDate;
     }
     
     const results = {
       talent: null,
       organiser: null
     };
     
     // Send notification to talent
     if (bookingData.talentUID) {
       results.talent = await this.sendNotification(
         bookingData.talentUID,
         talentSubject,
         talentBody,
         {
           ...notificationOptions,
           bookingId: bookingData.id,
           route: `/booking-management/${bookingData.talentUID}?bookingId=${bookingData.id}`,
           email: bookingData.talentEmail,
           userType: this.USER_TYPES.TALENT
         }
       );
     }
     
     // Send notification to organiser
     if (bookingData.organiserUID) {
       results.organiser = await this.sendNotification(
         bookingData.organiserUID,
         organiserSubject,
         organiserBody,
         {
           ...notificationOptions,
           bookingId: bookingData.id,
           route: `/booking-management/${bookingData.organiserUID}?bookingId=${bookingData.id}`,
           email: bookingData.organiserEmail,
           userType: this.USER_TYPES.ORGANISER
         }
       );
     }
     
     return {
       success: results.talent?.success || results.organiser?.success,
       results
     };
   } catch (error) {
     debug.error('NotificationService', 'Error sending notifications to booking participants', error);
     return { success: false, error: error.message };
   }
 }

 /**
  * Mark a notification as read
  * @param {string} notificationId - ID of the notification
  * @param {string} userId - User ID who owns the notification
  * @returns {Promise<Object>} Result of the operation
  */
 async markNotificationAsRead(notificationId, userId) {
   try {
     const notificationRef = doc(db, 'notifications', notificationId);
     await updateDoc(notificationRef, {
       isRead: true,
       status: this.STATUS.READ,
       readAt: serverTimestamp()
     });
     
     return { success: true };
   } catch (error) {
     debug.error('NotificationService', 'Error marking notification as read', error);
     return { success: false, error: error.message };
   }
 }

 /**
  * Mark all notifications as read for a user
  * @param {string} userId - User ID
  * @returns {Promise<Object>} Result of the operation
  */
 async markAllNotificationsAsRead(userId) {
   try {
     const notificationsRef = collection(db, 'notifications');
     const q = query(
       notificationsRef,
       where('userId', '==', userId),
       where('isRead', '==', false)
     );
     
     const querySnapshot = await getDocs(q);
     
     if (querySnapshot.empty) {
       return { success: true, count: 0 };
     }
     
     // Use a batch for better performance
     const batch = writeBatch(db);
     for (const docSnap of querySnapshot.docs) {
       const notificationRef = doc(db, 'notifications', docSnap.id);
       batch.update(notificationRef, {
         isRead: true,
         status: this.STATUS.READ,
         readAt: serverTimestamp()
       });
     }
     
     await batch.commit();
     
     return { success: true, count: querySnapshot.size };
   } catch (error) {
     debug.error('NotificationService', 'Error marking all notifications as read', error);
     return { success: false, error: error.message };
   }
 }

 /**
  * Delete a notification
  * @param {string} notificationId - ID of the notification
  * @param {string} userId - User ID who owns the notification
  * @returns {Promise<Object>} Result of the operation
  */
 async deleteNotification(notificationId, userId) {
   try {
     // Verify the notification belongs to the user
     const notificationsRef = collection(db, 'notifications');
     const q = query(
       notificationsRef,
       where('userId', '==', userId),
       where('__name__', '==', notificationId)
     );
     
     const querySnapshot = await getDocs(q);
     
     if (querySnapshot.empty) {
       return { success: false, error: 'Notification not found or access denied' };
     }
     
     // Delete the notification
     await deleteDoc(doc(db, 'notifications', notificationId));
     
     return { success: true };
   } catch (error) {
     debug.error('NotificationService', 'Error deleting notification', error);
     return { success: false, error: error.message };
   }
 }

 /**
 * Get user's notifications with pagination and optional filtering by userType
 * @param {string} userId - User ID
 * @param {number} limit - Number of notifications to fetch
 * @param {string} lastVisibleId - Last notification ID for pagination
 * @param {string} userType - Optional filter for user type (Talent or Organiser)
 * @returns {Promise<Object>} Notifications and pagination info
 */
async getUserNotifications(userId, limitCount = 20, lastVisibleId = null, userType = null) {
  try {
    const notificationsRef = collection(db, 'notifications');
    let q;
    
    // Build query with user type filtering if provided
    let baseQuery = [
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    ];
    
    // Add userType filter if provided
    if (userType && Object.values(this.USER_TYPES).includes(userType)) {
      baseQuery.push(where('userType', '==', userType));
    }
    
    // Build query with pagination if lastVisibleId is provided
    if (lastVisibleId) {
      const lastVisibleDocRef = doc(db, 'notifications', lastVisibleId);
      const lastVisibleDoc = await getDoc(lastVisibleDocRef);
      
      if (!lastVisibleDoc.exists()) {
        throw new Error('Invalid pagination reference');
      }
      
      q = query(
        notificationsRef,
        ...baseQuery,
        startAfter(lastVisibleDoc),
        limit(limitCount)
      );
    } else {
      q = query(
        notificationsRef,
        ...baseQuery,
        limit(limitCount)
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    const notifications = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      
      // Format any timestamp data in notifications
      let formattedData = {};
      if (data.data && data.data.eventDate) {
        formattedData = {
          ...data.data,
          formattedDate: formatDate(data.data.eventDate)
        };
      }
      
      notifications.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || null,
        readAt: data.readAt?.toDate() || null,
        data: {
          ...data.data,
          ...formattedData
        }
      });
    });
    
    // Determine if there are more notifications
    const hasMore = notifications.length === limitCount;
    const lastVisible = notifications.length > 0 ? notifications[notifications.length - 1].id : null;
    
    return {
      notifications,
      pagination: {
        hasMore,
        lastVisible,
        count: notifications.length
      }
    };
  } catch (error) {
    debug.error('NotificationService', 'Error getting user notifications', error);
    throw error;
  }
}

 /**
  * Get unread notification count for a user
  * @param {string} userId - User ID
  * @param {string} userType - Optional filter for user type (Talent or Organiser)
  * @returns {Promise<number>} Count of unread notifications
  */
 async getUnreadNotificationCount(userId, userType = null) {
  try {
    const notificationsRef = collection(db, 'notifications');
    let queryConstraints = [
      where('userId', '==', userId),
      where('isRead', '==', false)
    ];
    
    // Add userType filter if provided
    if (userType && Object.values(this.USER_TYPES).includes(userType)) {
      queryConstraints.push(where('userType', '==', userType));
    }
    
    const q = query(notificationsRef, ...queryConstraints);
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread notification count:', error);
    return 0;
  }
}

/**
 * Get user data for notification purposes
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User data or null if not found
 */
async getUserData(userId) {
  try {
    // Check cache first
    const cachedUser = cache.get(userId, 'users');
    if (cachedUser) {
      return cachedUser;
    }
    
    const userDoc = await getDoc(doc(db, 'Users', userId));
    
    if (!userDoc.exists()) {
      console.warn(`User not found: ${userId}`);
      return null;
    }
    
    const userData = userDoc.data();
    
    // Store in cache
    cache.set(userId, userData, 'users');
    
    return userData;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
}

/**
 * Get all notification templates (combines all template functions)
 * @param {string} type - Template type
 * @param {Object} data - Template data
 * @returns {Object|null} Template or null if not found
 */
getNotificationTemplate(type, data = {}) {
  // Check cache first
  const cacheKey = `${type}_${JSON.stringify(data)}`;
  const cachedTemplate = cache.get(cacheKey, 'templates');
  if (cachedTemplate) {
    return cachedTemplate;
  }
  
  // Check each template category
  let template = this.getBookingNotificationTemplate(type, data) ||
                 this.getPaymentNotificationTemplate(type, data) ||
                 this.getAgreementNotificationTemplate(type, data) ||
                 this.getSystemNotificationTemplate(type, data);
  
  if (template) {
    // Store in cache
    cache.set(cacheKey, template, 'templates');
  }
  
  return template;
}

/**
 * Get system notification templates (migrated from SystemNotificationService)
 * @param {string} type - Template type
 * @param {Object} data - Template data
 * @returns {Object|null} Template object or null if not found
 */
getSystemNotificationTemplate(type, data = {}) {
  const templates = {
    // Booking notification templates
    booking_request: {
      title: `New Booking Request: ${data.eventName || 'Event'}`,
      body: `You've received a new booking request from ${data.organiserName || 'an organiser'} for an event on ${data.eventDate || 'the specified date'}.`,
      category: this.CATEGORIES.BOOKING,
      priority: this.PRIORITIES.HIGH,
      forRecipient: this.USER_TYPES.TALENT,
      actions: [
        {
          label: 'View Request',
          url: `/booking-management/${data.talentUID}?bookingId=${data.bookingId}`,
          primary: true
        }
      ]
    },
    
    // Event reminder templates
    event_reminder: {
      title: `Event Reminder: ${data.eventName || 'Event'} (${data.timeFrame || 'Soon'})`,
      body: `Your ${data.userType === this.USER_TYPES.TALENT ? 'performance' : 'event'} at ${data.venue || 'the venue'} is coming up ${data.timeFrame ? `in ${data.timeFrame}` : 'soon'}.`,
      category: this.CATEGORIES.REMINDER,
      priority: data.hoursUntilEvent <= 24 ? this.PRIORITIES.HIGH : this.PRIORITIES.MEDIUM,
      actions: [
        {
          label: 'View Details',
          url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
          primary: true
        }
      ]
    },
    
    // Review notification templates
    review_request: {
      title: `Review Request: ${data.eventName || 'Event'}`,
      body: `Please take a moment to review your experience with ${
        data.userType === this.USER_TYPES.TALENT ? data.organiserName || 'the organiser' : data.talentName || 'the talent'
      }.`,
      category: this.CATEGORIES.REVIEW,
      priority: this.PRIORITIES.MEDIUM,
      actions: [
        {
          label: 'Leave Review',
          url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&review=true`,
          primary: true
        }
      ]
    },
    
    // Dispute notification templates
    dispute_initiated: {
      title: `Dispute Initiated: ${data.eventName || 'Event'}`,
      body: `A dispute has been initiated for your booking. Please respond with your perspective.`,
      category: this.CATEGORIES.DISPUTE,
      priority: this.PRIORITIES.HIGH,
      actions: [
        {
          label: 'View Dispute',
          url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=dispute`,
          primary: true
        }
      ]
    },
    dispute_resolved: {
      title: `Dispute Resolved: ${data.eventName || 'Event'}`,
      body: `The dispute for your booking has been resolved. ${data.resolution || ''}`,
      category: this.CATEGORIES.DISPUTE,
      priority: this.PRIORITIES.HIGH
    },

    event_updated: {
      title: `Event Updated: ${data.eventName || 'Event'}`,
      body: `The event "${data.eventName || 'Event'}" you applied to has been updated. ${data.changesSummary || 'Please review the changes and decide if you want to continue with your application.'}`,
      category: this.CATEGORIES.EVENT,
      priority: this.PRIORITIES.HIGH,
      actions: [
        {
          label: 'View Changes',
          action: 'view_event_changes',
          route: `/talent-gig-applications`
        },
        {
          label: 'Cancel Application',
          action: 'cancel_application',
          style: 'danger'
        }
      ]
    }
  };
  
  return templates[type] || null;
}

/**
 * Get notification templates for booking notifications
 * Each template can specify which user type it should be sent to using 'forRecipient'
 * @param {string} type - Notification type
 * @param {Object} data - Data to include in the template
 * @returns {Object|null} Template with subject and body
 */
getBookingNotificationTemplate(type, data = {}) {
 const templates = {
   booking_request_received: {
     subject: 'Yebo! New Booking Request',
     body: `You have received a new booking request for ${data.eventName || 'an event'} on ${data.formattedDate || data.date || 'the specified date'}. Please check your Booking Management page to review and respond.`,
     priority: this.PRIORITIES.HIGH,
     forRecipient: this.USER_TYPES.TALENT, // Only for talent
     actions: [
       {
         label: 'View Request',
         url: `/booking-management/${data.talentUID || data.userId}?bookingId=${data.bookingId}`,
         primary: true
       }
     ]
   },
   booking_request_sent: {
     subject: 'Booking Request Sent',
     body: `Your booking request for ${data.eventName || 'the event'} on ${data.formattedDate || data.date || 'the specified date'} has been sent to the talent. You'll be notified when they respond. Check your Booking Management page for updates.`,
     priority: this.PRIORITIES.MEDIUM,
     forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
     actions: [
       {
         label: 'View Request',
         url: `/booking-management/${data.organiserUID || data.userId}?bookingId=${data.bookingId}`,
         primary: true
       }
     ]
   },
   booking_accepted: {
     subject: 'Great News! Booking Request Accepted',
     body: `Fantastic! Your booking request for ${data.eventName || 'the event'} has been accepted. Please check your Booking Management page to view the next steps.`,
     priority: this.PRIORITIES.HIGH,
     forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
     actions: [
       {
         label: 'View Booking',
         url: `/booking-management/${data.organiserUID || data.userId}?bookingId=${data.bookingId}`,
         primary: true
       }
     ]
    },
    booking_request_declined: {
      subject: 'Booking Request Declined',
      body: `We're sorry, but your booking request for ${data.eventName || 'the event'} has been declined.${data.reason ? ` Reason: ${data.reason}` : ''} Don't worry, there are plenty more talented performers to choose from! Visit your Booking Management page to explore other options.`,
     priority: this.PRIORITIES.MEDIUM,
     forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
     actions: [
       {
         label: 'Find Talent',
         url: '/browse-talent',
         primary: true
       }
     ]
   },
   booking_cancelled: {
     subject: 'Booking Cancelled',
     body: `The booking for ${data.eventName || 'the event'} has been cancelled.${data.reason ? ` Reason: ${data.reason}` : ''} Please check your Booking Management page for details.`,
     priority: this.PRIORITIES.HIGH,
     // No forRecipient specified as this goes to both
     actions: [
       {
         label: 'View Details',
         url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
         primary: true
       }
     ]
   },
   booking_closed: {
    subject: 'Booking Closed',
    body: `The booking for ${data.eventName || 'the event'} has been closed and archived. Thank you for using Gigify! You can view all your past bookings in your Booking Management page.`,
    priority: this.PRIORITIES.MEDIUM,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Bookings',
        url: `/booking-management/${data.userId}`,
        primary: true
      }
    ]
  },
  booking_completed: {
    subject: 'Booking Completed - Howzit!',
    body: `The booking for ${data.eventName || 'the event'} has been marked as completed. Thank you for using Gigify! You can view your completed booking in your Booking Management page.`,
    priority: this.PRIORITIES.MEDIUM,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Booking',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  booking_reminder: {
    subject: `Upcoming Booking: ${data.eventName || 'Event'} - ${data.daysAway || ''} Day Reminder`,
    body: `Just a friendly reminder about your upcoming booking for ${data.eventName || 'the event'} on ${data.formattedDate || data.date || 'the specified date'}. Check your Booking Management page for all event details.`,
    priority: this.PRIORITIES.MEDIUM,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Details',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  booking_change: {
    subject: 'Booking Details Updated',
    body: `The details for your booking (${data.eventName || 'event'}) have been updated. Please check your Booking Management page for the latest information.`,
    priority: this.PRIORITIES.HIGH,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Updates',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  event_updated: {
    subject: `Event Updated: ${data.eventName || 'Event'}`,
    body: `The event "${data.eventName || 'Event'}" you applied to has been updated.

Changes made: ${data.changesSummary || 'Multiple fields updated'}

${data.changesDetail || ''}

You can review the changes and decide whether to continue with your application. If these changes affect your ability to perform, you can cancel your application.

View your applications: https://gigify.co.za/talent-gig-applications`,
    priority: this.PRIORITIES.HIGH,
    forRecipient: this.USER_TYPES.TALENT, // Only for talent
    actions: [
      {
        label: 'View Applications',
        url: `/talent-gig-applications`,
        primary: true
      }
    ]
  },
  agreement_generated: {
    subject: 'Performance Agreement Generated',
    body: `A performance agreement has been generated for your booking (${data.eventName || 'event'}). Please visit your Booking Management page to review and sign the agreement.`,
    priority: this.PRIORITIES.MEDIUM,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Agreement',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
        primary: true
      }
    ]
  },
  booking_disputed: {
    subject: 'Booking Disputed',
    body: `A dispute has been opened for the booking (${data.eventName || 'event'}).${data.reason ? ` Reason: ${data.reason}` : ''} Please check your Booking Management page for details and required actions.`,
    priority: this.PRIORITIES.HIGH,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Dispute',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=dispute`,
        primary: true
      }
    ]
  },
  force_majeure_claimed: {
    subject: 'Force Majeure Claimed',
    body: `A force majeure claim has been made for the booking (${data.eventName || 'event'}).${data.reason ? ` Reason: ${data.reason}` : ''} Please check your Booking Management page for more information.`,
    priority: this.PRIORITIES.HIGH,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Details',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  event_completed_review_request: {
    subject: 'Please Leave a Review',
    body: `Your booking for ${data.eventName || 'the event'} is now complete. Please take a moment to visit your Booking Management page to leave a review - your feedback helps our community thrive!`,
    priority: this.PRIORITIES.LOW,
    category: this.CATEGORIES.REVIEW,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'Leave Review',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&review=true`,
        primary: true
      }
    ]
  },
  agreement_signed: {
    subject: data.userType === 'organiser' ? 'Agreement Signed - Payment Required' : 'Agreement Signed - Payment Pending',
    body: data.userType === 'organiser' 
      ? `Excellent! The performance agreement for ${data.eventName || 'the event'} has been signed by both parties. You now need to secure your booking by making the TradeSafe Escrow payment of R${data.bookingFee || 0}. Payment must be completed by ${data.feePaymentDueDate ? new Date(data.feePaymentDueDate.seconds * 1000).toLocaleDateString('en-ZA') : 'the due date'} to confirm your booking.`
      : `Great news! The performance agreement for ${data.eventName || 'the event'} has been signed by both parties. The organiser now needs to complete the TradeSafe Escrow payment of R${data.bookingFee || 0} by ${data.feePaymentDueDate ? new Date(data.feePaymentDueDate.seconds * 1000).toLocaleDateString('en-ZA') : 'the due date'} to secure the booking.`,
    priority: this.PRIORITIES.HIGH,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: data.userType === 'organiser' ? 'Make Payment' : 'View Booking',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  agreement_generated: {
    subject: 'Performance Agreement Ready for Signing',
    body: `The performance agreement for ${data.eventName || 'the event'} has been generated and is ready for your signature. Please check your Booking Management page to review and sign.`,
    priority: this.PRIORITIES.HIGH,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'Sign Agreement',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  agreement_ready: {
    subject: 'Agreement Ready for Signing',
    body: `The performance agreement for ${data.eventName || 'the event'} is ready for your signature. Please check your Booking Management page to review and sign the agreement.`,
    priority: this.PRIORITIES.HIGH,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'Sign Agreement',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  talent_performance_complete_review_request: {
    subject: 'Performance Complete - Please Leave a Review',
    body: `The performance for ${data.eventName || 'the event'} has been completed. Please take a moment to leave a review about your experience.`,
    priority: this.PRIORITIES.MEDIUM,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'Leave Review',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&review=true`,
        primary: true
      }
    ]
  },
  payment_complete: {
    subject: 'Payment Complete - Booking Confirmed!',
    body: `Excellent! Your payment for ${data.eventName || 'the event'} has been successfully processed and secured in escrow. Your booking is now confirmed. Check your Booking Management page for all the details.`,
    priority: this.PRIORITIES.HIGH,
    forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
    actions: [
      {
        label: 'View Booking',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  }
};

return templates[type] || null;
}

/**
* Get notification templates for payment notifications
* Each template can specify which user type it should be sent to using 'forRecipient'
* @param {string} type - Notification type
* @param {Object} data - Data to include in the template
* @returns {Object|null} Template with subject and body
*/
getPaymentNotificationTemplate(type, data = {}) {
const templates = {
  payment_initiated: {
    subject: 'Payment Initiated',
    body: `A payment of R${data.amount || ''} has been initiated for your booking (${data.eventName || 'event'}). Please check your Booking Management page to complete the payment process.`,
    priority: this.PRIORITIES.HIGH,
    forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
    actions: [
      {
        label: 'Complete Payment',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  payment_successful: {
    subject: 'Payment Successful - Shot!',
    body: `Your payment of R${data.amount || ''} for the booking (${data.eventName || 'event'}) has been successfully processed. View the updated status in your Booking Management page.`,
    priority: this.PRIORITIES.HIGH,
    // No forRecipient specified as this goes to both
    actions: [
      {
        label: 'View Details',
        url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
        primary: true
      }
    ]
  },
  payment_failed: {
   subject: 'Payment Failed',
   body: `Eish! We encountered an issue processing your payment of R${data.amount || ''} for the booking (${data.eventName || 'event'}). Please visit your Booking Management page to try again.`,
   priority: this.PRIORITIES.URGENT,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'Retry Payment',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_reminder: {
   subject: 'Payment Reminder',
   body: `Just a reminder that payment of R${data.amount || ''} is due for your booking (${data.eventName || 'event'}). Please visit your Booking Management page to complete payment.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'Make Payment',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_released: {
   subject: 'Payment Released - Lekker!',
   body: `The payment of R${data.amount || ''} for your booking (${data.eventName || 'event'}) has been released to your account. You can check the payment details in your Booking Management page.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.TALENT, // Only for talent
   actions: [
     {
       label: 'View Details',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_refunded: {
   subject: 'Payment Refunded',
   body: `Your payment of R${data.amount || ''} for the booking (${data.eventName || 'event'}) has been refunded. You can view the refund details in your Booking Management page.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'View Details',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_disputed: {
   subject: 'Payment Disputed',
   body: `A dispute has been opened for the payment of R${data.amount || ''} for your booking (${data.eventName || 'event'}). Please check your Booking Management page for details and required actions.`,
   priority: this.PRIORITIES.URGENT,
   category: this.CATEGORIES.DISPUTE,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'View Dispute',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=dispute`,
       primary: true
     }
   ]
 },
 payment_in_escrow: {
   subject: 'Payment Secured in Escrow',
   body: `Good news! The payment of R${data.amount || ''} for your booking (${data.eventName || 'event'}) has been secured in escrow. You can view the booking status in your Booking Management page.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.TALENT, // Only for talent
   actions: [
     {
       label: 'View Status',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 transport_payment_needed: {
   subject: 'Transportation Payment Required',
   body: `A transportation payment of R${data.amount || ''} is required for your booking (${data.eventName || 'event'}). Please visit your Booking Management page to complete this payment.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'Make Payment',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_required: {
   subject: 'Payment Required',
   body: `Payment is required for your booking (${data.eventName || 'event'}). Please check your Booking Management page to complete your payment and confirm the booking.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'Make Payment',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_complete: {
   subject: 'Payment Complete - All Sorted!',
   body: `We've received your payment for ${data.eventName || 'the event'}. Your booking is now confirmed. View all the details in your Booking Management page.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'View Booking',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_pending: {
   subject: 'Payment Pending',
   body: `A payment is pending for your booking (${data.eventName || 'event'}). Please check your Booking Management page for payment status and details.`,
   priority: this.PRIORITIES.MEDIUM,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'View Status',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 logistics_payment_released: {
   subject: 'Logistics Payment Released',
   body: `The logistics payment of R${data.amount || ''} for your booking (${data.eventName || 'event'}) has been released. You can view the payment details in your Booking Management page.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.TALENT, // Only for talent
   actions: [
     {
       label: 'View Details',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 payment_pending: {
   subject: 'Payment Pending',
   body: `Your booking for ${data.eventName || 'the event'} is awaiting payment. The payment process has been initiated and we'll notify you once it's completed.`,
   priority: this.PRIORITIES.MEDIUM,
   forRecipient: this.USER_TYPES.TALENT, // Only for talent
   actions: [
     {
       label: 'View Status',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 }
};

return templates[type] || null;
}

/**
* Get notification templates for agreement notifications
* Each template can specify which user type it should be sent to using 'forRecipient'
* @param {string} type - Notification type
* @param {Object} data - Data to include in the template
* @returns {Object|null} Template with subject and body
*/
getAgreementNotificationTemplate(type, data = {}) {
const templates = {
 agreement_generated: {
   subject: 'Performance Agreement Generated',
   body: `A performance agreement has been generated for your booking (${data.eventName || 'event'}). Please visit your Booking Management page to review and sign the agreement.`,
   priority: this.PRIORITIES.MEDIUM,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'View Agreement',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 },
 agreement_ready: {
   subject: 'URGENT: Performance Agreement Ready for Signature',
   body: `Your performance agreement for ${data.eventName || 'the event'} is ready for your signature. Please visit your Booking Management page to sign the agreement.

⚠️ IMPORTANT: This agreement must be signed within 2 days (48 hours), or it will expire and need to be regenerated.`,
   priority: this.PRIORITIES.HIGH,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'Sign Agreement',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 },
 agreement_signed_talent: {
   subject: 'Talent Signed the Agreement',
   body: `The talent has signed the performance agreement for your booking (${data.eventName || 'event'}). Please check your Booking Management page to complete your signature and finalize the agreement.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.ORGANISER, // Only for organiser
   actions: [
     {
       label: 'Sign Agreement',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 },
 agreement_signed_organiser: {
   subject: 'Organiser Signed the Agreement',
   body: `The organiser has signed the performance agreement for your booking (${data.eventName || 'event'}). Please check your Booking Management page to complete your signature and finalize the agreement.`,
   priority: this.PRIORITIES.HIGH,
   forRecipient: this.USER_TYPES.TALENT, // Only for talent
   actions: [
     {
       label: 'Sign Agreement',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 },
 agreement_completed: {
   subject: 'Performance Agreement Fully Signed',
   body: `Great news! The performance agreement for your booking (${data.eventName || 'event'}) has been signed by all parties. You can view the completed agreement in your Booking Management page.`,
   priority: this.PRIORITIES.HIGH,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'View Agreement',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 },
 agreement_rejected: {
   subject: 'Agreement Rejected',
   body: `The performance agreement for your booking (${data.eventName || 'event'}) has been rejected.${data.reason ? ` Reason: ${data.reason}` : ''} Please check your Booking Management page for more information.`,
   priority: this.PRIORITIES.HIGH,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'View Details',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 },
 agreement_reminder: {
   subject: 'URGENT: Sign Performance Agreement - Time is Running Out!',
   body: `This is a reminder to sign the performance agreement for your booking (${data.eventName || 'event'}). Please visit your Booking Management page to complete the signing process.
   
Please note that unsigned agreements automatically expire after 2 days (48 hours). If the agreement expires, the booking request will need to be submitted again from scratch.`,
   priority: this.PRIORITIES.HIGH,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'Sign Now',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 },
 agreement_expired: {
   subject: 'Agreement Signature Expired',
   body: `The signing period for the performance agreement (${data.eventName || 'event'}) has expired. 

To proceed with this booking, the organiser must resubmit a booking request form to start the booking process again. Please check your Booking Management page for more information.`,
   priority: this.PRIORITIES.HIGH,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'View Details',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}`,
       primary: true
     }
   ]
 },
 agreement_signed: {
   subject: 'Agreement Signed - Let\'s Get Started!',
   body: `The performance agreement for ${data.eventName || 'the event'} has been signed by all parties. You can view the completed agreement in your Booking Management page.`,
   priority: this.PRIORITIES.HIGH,
   // No forRecipient specified as this goes to both
   actions: [
     {
       label: 'View Agreement',
       url: `/booking-management/${data.userId}?bookingId=${data.bookingId}&tab=agreement`,
       primary: true
     }
   ]
 }
};

return templates[type] || null;
}

/**
* Schedule a notification for future delivery
* @param {string} userId - User ID of the recipient
* @param {string} userType - Type of user (Talent or Organiser)
* @param {string} title - Notification title
* @param {string} body - Notification body
* @param {Date} scheduledFor - When to deliver the notification
* @param {Object} options - Additional notification options
* @returns {Promise<Object>} Result of scheduling
*/
async scheduleNotification(userId, userType, title, body, scheduledFor, options = {}) {
try {
 // Validate user type
 if (!Object.values(this.USER_TYPES).includes(userType)) {
   throw new Error(`Invalid user type: ${userType}. Must be either 'Talent' or 'Organiser'`);
 }
 
 // Ensure scheduledFor is a valid future date
 const scheduledDate = parseFirebaseDate(scheduledFor);
 const now = new Date();
 
 if (!scheduledDate || scheduledDate <= now) {
   throw new Error('Scheduled date must be in the future');
 }
 
 // Format any date information in options
 const formattedOptions = { ...options };
 if (options.data && options.data.eventDate) {
   formattedOptions.data = {
     ...options.data,
     formattedDate: formatDate(options.data.eventDate)
   };
 }
 
 // Set appropriate route if bookingId is provided
 if (options.bookingId && !options.route) {
   formattedOptions.route = `/booking-management/${userId}?bookingId=${options.bookingId}`;
 }
 
 // Create notification with scheduled delivery date
 return await this.sendNotification(userId, title, body, {
   ...formattedOptions,
   scheduledFor: scheduledDate,
   userType
 });
} catch (error) {
 console.error('Error scheduling notification:', error);
 return { success: false, error: error.message };
}
}
/**
* Process scheduled notifications that are due
* Should be called by a scheduled function (e.g., Cloud Function)
* @returns {Promise<Object>} Processing results
*/
async processScheduledNotifications() {
  try {
    const now = new Date();
    const notificationsRef = collection(db, 'notifications');
    
    // Query for scheduled notifications that are due
    const q = query(
      notificationsRef,
      where('scheduledFor', '<=', now),
      where('status', '==', null)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      debug.log('NotificationService', 'No scheduled notifications due at this time');
      return { success: true, count: 0 };
    }
    
    debug.log('NotificationService', `Processing ${querySnapshot.size} scheduled notifications`);
    
    const processedNotifications = [];
    
    for (const notificationDoc of querySnapshot.docs) {
      const notification = notificationDoc.data();
      const notificationId = notificationDoc.id;
      
      try {
        // Process notification by sending it through appropriate channels
        const result = await this.deliverScheduledNotification(
         notification, notificationId);
         processedNotifications.push({ id: notificationId, success: true, result });
       } catch (error) {
         debug.error('NotificationService', `Error processing scheduled notification ${notificationId}`, error);
         
         // Update notification with error status
         await updateDoc(doc(db, 'notifications', notificationId), {
           status: this.STATUS.FAILED,
           error: error.message,
           processedAt: serverTimestamp()
         });
         
         processedNotifications.push({ id: notificationId, success: false, error: error.message });
       }
     }
     
     return { success: true, count: processedNotifications.length, notifications: processedNotifications };
   } catch (error) {
     debug.error('NotificationService', 'Error processing scheduled notifications', error);
     return { success: false, error: error.message };
   }
  }
  
  /**
  * Deliver a scheduled notification
  * @param {Object} notification - The notification data
  * @param {string} notificationId - The notification ID
  * @returns {Promise<Object>} Delivery result
  */
  async deliverScheduledNotification(notification, notificationId) {
   try {
     const { userId, title, body, category, priority, bookingId, route, data, userType } = notification;
     
     // Get user's email
     let userEmail = null;
     const userDoc = await this.getUserData(userId);
     if (userDoc) {
       userEmail = userDoc.profile?.email;
     }
     
     // Process any date fields in the data
     const processedData = { ...data };
     if (data && data.eventDate) {
       processedData.formattedDate = formatDate(data.eventDate);
     }
     
     // Update route if needed to point to booking management
     let updatedRoute = route;
     if (bookingId && (!route || route.includes('booking-details') || route.includes('talent-confirmed-bookings'))) {
       updatedRoute = `/booking-management/${userId}?bookingId=${bookingId}`;
     }
     
     // Send through both channels by default
     const channelResults = {};
     
     // For in-app notification, we just need to update the existing record
     channelResults.inApp = { success: true };
     
     // Send email notification if user email is available
     if (userEmail) {
       try {
         await this.sendEmailNotification(userEmail, title, body, {
           type: category,
           bookingId,
           userType,
           ...processedData
         });
         channelResults.email = { success: true };
       } catch (error) {
         debug.error('NotificationService', `Error sending email for scheduled notification ${notificationId}`, error);
         channelResults.email = { success: false, error: error.message };
       }
     }
     
     // Update notification record with delivery status
     const overallSuccess = channelResults.inApp?.success || channelResults.email?.success;
     
     await updateDoc(doc(db, 'notifications', notificationId), {
       status: overallSuccess ? this.STATUS.DELIVERED : this.STATUS.FAILED,
       channelResults,
       processedAt: serverTimestamp(),
       updatedAt: serverTimestamp(),
       route: updatedRoute // Update route to use booking management
     });
     
     return {
       success: overallSuccess,
       notificationId,
       channelResults
     };
   } catch (error) {
     debug.error('NotificationService', `Error delivering scheduled notification ${notificationId}`, error);
     
     // Update notification with error status
     await updateDoc(doc(db, 'notifications', notificationId), {
       status: this.STATUS.FAILED,
       error: error.message,
       processedAt: serverTimestamp(),
       updatedAt: serverTimestamp()
     });
     
     throw error;
   }
  }
  
  /**
  * Send a review reminder notification to a user
  * @param {string} userId - User ID
  * @param {string} userType - Type of user (Talent or Organiser)
  * @param {string} bookingId - Booking ID
  * @param {Object} data - Additional data for the notification
  * @returns {Promise<Object>} Result of the notification sending
  */
  async sendReviewReminder(userId, userType, bookingId, data = {}) {
   try {
     // Validate user type
     if (!Object.values(this.USER_TYPES).includes(userType)) {
       throw new Error(`Invalid user type: ${userType}. Must be either 'Talent' or 'Organiser'`);
     }
     
     // Get booking data to construct a more detailed notification
     const bookingRef = doc(db, 'Bookings', bookingId);
     const bookingSnap = await getDoc(bookingRef);
     
     if (!bookingSnap.exists()) {
       throw new Error(`Booking not found: ${bookingId}`);
     }
     
     const bookingData = bookingSnap.data();
     
     // Get counterparty name for personalized reminder
     const isOrganiser = userType === this.USER_TYPES.ORGANISER;
     const counterpartyName = isOrganiser 
       ? bookingData.talentName || 'the talent'
       : bookingData.organiserName || 'the organiser';
     
     // Get event name for contextual relevance
     const eventName = bookingData.Event?.eventName || bookingData.event?.eventName || 'your event';
     
     // Format event date if present
     let formattedDate = null;
     if (bookingData.event?.date) {
       formattedDate = formatDate(bookingData.event.date);
     } else if (bookingData.Event?.date) {
       formattedDate = formatDate(bookingData.Event.date);
     }
     
     // Send the notification
     return await this.sendNotification(
       userId,
       'Please Leave a Review',
       `Your booking for "${eventName}" ${formattedDate ? `on ${formattedDate}` : ''} has been completed. Share your experience with ${counterpartyName} - your feedback helps build our community! Visit your Booking Management page to leave a review.`,
       {
         category: this.CATEGORIES.REVIEW,
         priority: this.PRIORITIES.MEDIUM,
         channel: this.CHANNELS.BOTH,
         bookingId,
         route: `/booking-management/${userId}?bookingId=${bookingId}&review=true`,
         userType,
         data: {
           eventName,
           counterpartyName,
           counterpartyType: isOrganiser ? 'talent' : 'organiser',
           formattedDate
         },
         actions: [
           {
             label: 'Leave a Review',
             url: `/booking-management/${userId}?bookingId=${bookingId}&review=true`,
             primary: true
           }
         ]
       }
     );
   } catch (error) {
     debug.error('NotificationService', 'Error sending review reminder', error);
     return { success: false, error: error.message };
   }
  }
  
  /**
  * Send a dispute notification
  * @param {string} recipientId - User ID of the recipient
  * @param {string} recipientType - Type of recipient (Talent or Organiser)
  * @param {string} disputeType - Type of dispute notification
  * @param {string} bookingId - ID of the booking
  * @param {Object} data - Additional data for the notification
  * @returns {Promise<Object>} Result of the notification sending
  */
  async sendDisputeNotification(recipientId, recipientType, disputeType, bookingId, data = {}) {
   try {
     // Validate recipient type
     if (!Object.values(this.USER_TYPES).includes(recipientType)) {
       throw new Error(`Invalid recipient type: ${recipientType}. Must be either 'Talent' or 'Organiser'`);
     }
     
     // Format any date information in the data
     const formattedData = { ...data };
     if (data.eventDate) {
       formattedData.formattedDate = formatDate(data.eventDate);
     }
     
     // Create templates for different dispute notification types
     const templates = {
       dispute_initiated: {
         subject: 'Dispute Initiated - Attention Required',
         body: `A dispute has been initiated for your booking ${data.eventName ? `(${data.eventName})` : ''}. Please check your Booking Management page for details.${data.reason ? ` Reason: ${data.reason}` : ''}`
       },
       dispute_response_required: {
         subject: 'Response Required: Booking Dispute',
         body: `Please respond to the dispute for your booking ${data.eventName ? `(${data.eventName})` : ''} within the next 48 hours. Visit your Booking Management page to submit your response.`
       },
       dispute_resolved: {
         subject: 'Dispute Resolved',
         body: `The dispute for your booking ${data.eventName ? `(${data.eventName})` : ''} has been resolved. Check your Booking Management page for details.${data.resolution ? ` Resolution: ${data.resolution}` : ''}`
       },
       dispute_escalated: {
         subject: 'Dispute Escalated to Gigify',
         body: `The dispute for your booking ${data.eventName ? `(${data.eventName})` : ''} has been escalated for review by Gigify. You can track the status in your Booking Management page.`
       }
     };
     
     const template = templates[disputeType];
     if (!template) {
       throw new Error(`Unknown dispute notification type: ${disputeType}`);
     }
     
     // Set appropriate route to booking management with dispute tab
     const route = `/booking-management/${recipientId}?bookingId=${bookingId}&tab=dispute`;
     
     // Send notification
     return await this.sendNotification(
       recipientId,
       template.subject,
       template.body,
       {
         category: this.CATEGORIES.DISPUTE,
         priority: this.PRIORITIES.URGENT,
         channel: this.CHANNELS.BOTH,
         bookingId,
         route,
         userType: recipientType,
         data: formattedData,
         actions: [
          {
            label: 'View Dispute',
            url: route,
            primary: true
          }
        ]
       }
     );
   } catch (error) {
     debug.error('NotificationService', 'Error sending dispute notification', error);
     return { success: false, error: error.message };
   }
  }
  
  /**
  * Send a performance reminder notification before an event
  * @param {string} userId - User ID
  * @param {string} userType - 'Talent' or 'Organiser'
  * @param {string} bookingId - Booking ID
  * @param {number} hoursUntilEvent - Hours until the event starts
  * @param {Object} eventDetails - Event details for personalized message
  * @returns {Promise<Object>} Result of the notification sending
  */
  async sendPerformanceReminder(userId, userType, bookingId, hoursUntilEvent, eventDetails) {
   try {
     // Validate user type
     if (!Object.values(this.USER_TYPES).includes(userType)) {
       throw new Error(`Invalid user type: ${userType}. Must be either 'Talent' or 'Organiser'`);
     }
     
     // Format the reminder time in a human-readable way
     let timeFrame;
     if (hoursUntilEvent >= 24) {
       const days = Math.floor(hoursUntilEvent / 24);
       timeFrame = `${days} ${days === 1 ? 'day' : 'days'}`;
     } else {
       timeFrame = `${hoursUntilEvent} ${hoursUntilEvent === 1 ? 'hour' : 'hours'}`;
     }
     
     // Format event date if present
     let formattedDate = null;
     if (eventDetails.eventDate) {
       formattedDate = formatDate(eventDetails.eventDate);
     }
     
     // Create different messages based on user type and time frame
     let title, body;
     
     if (userType === this.USER_TYPES.TALENT) {
       if (hoursUntilEvent <= 24) {
         title = `Performance Tomorrow: ${eventDetails.eventName || 'Upcoming Event'}`;
         body = `Reminder: Your performance at ${eventDetails.venue || 'the venue'} is tomorrow. Check your Booking Management page for all details, technical requirements and travel arrangements.`;
       } else if (hoursUntilEvent <= 4) {
         title = `Performance Soon: ${eventDetails.eventName || 'Upcoming Event'}`;
         body = `Your performance starts in ${timeFrame}. Make sure you're ready and on your way to ${eventDetails.venue || 'the venue'}. All details are available in your Booking Management page.`;
       } else {
         title = `Upcoming Performance: ${eventDetails.eventName || 'Event'} (${timeFrame})`;
         body = `Your performance at ${eventDetails.venue || 'the venue'} is coming up in ${timeFrame}. Check your Booking Management page to review all event details and make sure your preparations are on track.`;
       }
     } else {
       // Organiser reminders
       if (hoursUntilEvent <= 24) {
         title = `Event Tomorrow: ${eventDetails.eventName || 'Your Event'}`;
         body = `Reminder: Your event at ${eventDetails.venue || 'the venue'} is tomorrow. Check your Booking Management page to ensure everything is ready for the talent's arrival.`;
       } else if (hoursUntilEvent <= 4) {
         title = `Event Starting Soon: ${eventDetails.eventName || 'Your Event'}`;
         body = `Your event starts in ${timeFrame}. Visit your Booking Management page to review details and confirm the talent is on the way.`;
       } else {
         title = `Upcoming Event: ${eventDetails.eventName || 'Your Event'} (${timeFrame})`;
         body = `Your event at ${eventDetails.venue || 'the venue'} is coming up in ${timeFrame}. Check your Booking Management page to make sure your preparations are on track.`;
       }
     }
     
     // Set route to booking management with the specific booking selected
     const route = `/booking-management/${userId}?bookingId=${bookingId}`;
     
     // Send the notification
     return await this.sendNotification(
       userId,
       title,
       body,
       {
         category: this.CATEGORIES.REMINDER,
         priority: hoursUntilEvent <= 24 ? this.PRIORITIES.HIGH : this.PRIORITIES.MEDIUM,
         channel: this.CHANNELS.BOTH,
         bookingId,
         route,
         userType,
         data: {
           eventName: eventDetails.eventName,
           venue: eventDetails.venue,
           timeFrame,
           hoursUntilEvent,
           formattedDate
         },
         actions: [
          {
            label: 'View Event Details',
            url: route,
            primary: true
          }
        ]
       }
     );
   } catch (error) {
     debug.error('NotificationService', 'Error sending performance reminder', error);
     return { success: false, error: error.message };
   }
  }
  
  /**
  * Send system notifications to multiple users
  * @param {Array<Object>} recipients - Array of objects containing userId and userType
  * @param {string} title - Notification title
  * @param {string} body - Notification body
  * @param {Object} options - Additional notification options
  * @returns {Promise<Object>} Results of sending notifications
  */
  async sendBulkNotifications(recipients, title, body, options = {}) {
    try {
      const results = [];
      const batchSize = options.batchSize || 10; // Process in batches of 10 by default
      
      // Format any date information in options
      const formattedOptions = { ...options };
      if (options.data && options.data.eventDate) {
        formattedOptions.data = {
          ...options.data,
          formattedDate: formatDate(options.data.eventDate)
        };
      }
      
      // Update routing URLs for booking management
      if (options.bookingId && !options.route) {
        // Instead of using old routes, we'll use booking management
        formattedOptions.useBookingManagement = true;
      }
      
      // Process recipients in batches to avoid rate limits
      for (let i = 0; i < recipients.length; i += batchSize) {
        const batch = recipients.slice(i, Math.min(i + batchSize, recipients.length));
        
        // Process batch in parallel
        const batchResults = await Promise.all(batch.map(async (recipient) => {
          const { userId, userType } = recipient;
          
          // Validate user type
          if (!Object.values(this.USER_TYPES).includes(userType)) {
            debug.warn('NotificationService', `Skipping notification for invalid user type: ${userType}`);
            return {
              userId,
              success: false,
              error: `Invalid user type: ${userType}`
            };
          }
          
          // Set route to booking management if needed
          let route = formattedOptions.route;
          if (formattedOptions.useBookingManagement && options.bookingId) {
            route = `/booking-management/${userId}?bookingId=${options.bookingId}`;
          }
          
          try {
            // Apply rate limiting
            await this.applyRateLimit(userId);
            
            const result = await this.sendNotification(userId, title, body, {
              ...formattedOptions,
              userType,
              route
            });
            
            return {
              userId,
              userType,
              success: result.success,
              notificationId: result.notificationId
            };
          } catch (error) {
            debug.error('NotificationService', `Error sending notification to user ${userId}`, error);
            return {
              userId,
              userType,
              success: false,
              error: error.message
            };
          }
        }));
        
        results.push(...batchResults);
        
        // Add a small delay between batches
        if (i + batchSize < recipients.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      return {
        success: results.some(r => r.success),
        totalCount: recipients.length,
        successCount: results.filter(r => r.success).length,
        failCount: results.filter(r => !r.success).length,
        results
      };
    } catch (error) {
      debug.error('NotificationService', 'Error sending bulk notifications', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Create and send a custom notification
   * @param {string} userId - User ID
   * @param {string} userType - Type of user (Talent or Organiser)
   * @param {Object} notificationData - Custom notification data
   * @returns {Promise<Object>} Result of sending the notification
   */
  async createCustomNotification(userId, userType, notificationData) {
    try {
      // Validate user type
      if (!Object.values(this.USER_TYPES).includes(userType)) {
        throw new Error(`Invalid user type: ${userType}. Must be either 'Talent' or 'Organiser'`);
      }
      
      const { title, body, category, priority, channel, bookingId, route, scheduledFor } = notificationData;
      
      if (!title || !body) {
        throw new Error('Title and body are required for custom notifications');
      }
      
      // Format any date information in the notification data
      const formattedData = { ...notificationData.data || {} };
      if (formattedData.eventDate) {
        formattedData.formattedDate = formatDate(formattedData.eventDate);
      }
      
      // Validate category
      const validCategory = Object.values(this.CATEGORIES).includes(category);
      const finalCategory = validCategory ? category : this.CATEGORIES.SYSTEM;
      
      // Validate priority
      const validPriority = Object.values(this.PRIORITIES).includes(priority);
      const finalPriority = validPriority ? priority : this.PRIORITIES.MEDIUM;
      
      // Validate channel
      const validChannel = Object.values(this.CHANNELS).includes(channel);
      const finalChannel = validChannel ? channel : this.CHANNELS.BOTH;
      
      // Process scheduled date if provided
      let parsedScheduledDate = null;
      if (scheduledFor) {
        parsedScheduledDate = parseFirebaseDate(scheduledFor);
        if (!parsedScheduledDate) {
          throw new Error('Invalid scheduled date format');
        }
      }
      
      // Update route to use booking management if bookingId is provided
      let finalRoute = route;
      if (bookingId && (!route || route.includes('booking-details') || route.includes('signature-agreement'))) {
        finalRoute = `/booking-management/${userId}?bookingId=${bookingId}`;
      }
      
      return await this.sendNotification(userId, title, body, {
        category: finalCategory,
        priority: finalPriority,
        channel: finalChannel,
        bookingId,
        route: finalRoute,
        userType,
        scheduledFor: parsedScheduledDate,
        data: formattedData
      });
    } catch (error) {
      debug.error('NotificationService', 'Error creating custom notification', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Send a direct message notification between users
   * @param {string} senderId - User ID of sender
   * @param {string} senderType - Type of sender (Talent or Organiser)
   * @param {string} recipientId - User ID of recipient
   * @param {string} recipientType - Type of recipient (Talent or Organiser)
   * @param {string} message - Message content
   * @param {string} bookingId - Optional booking ID for context
   * @returns {Promise<Object>} Result of sending the notification
   */
  async sendMessageNotification(senderId, senderType, recipientId, recipientType, message, bookingId = null) {
    try {
      // Validate user types
      if (!Object.values(this.USER_TYPES).includes(senderType) || 
          !Object.values(this.USER_TYPES).includes(recipientType)) {
        throw new Error(`Invalid user type. Must be either 'Talent' or 'Organiser'`);
      }
      
      // Get sender data for the notification
      const senderData = await this.getUserData(senderId);
      if (!senderData) {
        throw new Error(`Sender not found: ${senderId}`);
      }
      
      // Get sender name (try different possible fields)
      const senderName = senderData.profile?.stageName || 
                         senderData.profile?.businessName ||
                         `${senderData.profile?.firstName || ''} ${senderData.profile?.lastName || ''}`.trim() ||
                         'User';
      
      // Set appropriate route - if booking related, go to booking management, otherwise inbox
      let route;
      if (bookingId) {
        route = `/booking-management/${recipientId}?bookingId=${bookingId}`;
      } else {
        // If not booking related, direct to inbox or messages page
        route = recipientType === this.USER_TYPES.TALENT ? 
          `/talent-inbox/${recipientId}` : 
          `/organiser-inbox/${recipientId}`;
      }
      
      // Create notification with message preview (truncate if too long)
      const messagePreview = message.length > 100 ? `${message.substring(0, 97)}...` : message;
      
      // Send notification
      return await this.sendNotification(
        recipientId,
        `New message from ${senderName}`,
        messagePreview,
        {
          category: this.CATEGORIES.MESSAGE,
          priority: this.PRIORITIES.MEDIUM,
          channel: this.CHANNELS.BOTH,
          bookingId,
          route,
          userType: recipientType,
          data: {
            senderId,
            senderName,
            senderType,
            fullMessage: message
          },
          actions: [
            {
              label: 'Reply',
              url: route,
              primary: true
            }
          ]
        }
      );
    } catch (error) {
      debug.error('NotificationService', 'Error sending message notification', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Clean up old notifications to maintain performance
   * @param {number} daysToKeep - Number of days to keep notifications before archiving
   * @returns {Promise<Object>} Result of the cleanup operation
   */
  async cleanupOldNotifications(daysToKeep = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
      
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('createdAt', '<', Timestamp.fromDate(cutoffDate)),
        // We can batch up to 500 writes in a single batch
        limit(450)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        debug.log('NotificationService', 'No old notifications to clean up');
        return { success: true, count: 0 };
      }
      
      debug.log('NotificationService', `Found ${querySnapshot.size} old notifications to archive`);
      
      // Create a batch for the write operations
      const batch = writeBatch(db);
      
      // Get document references for all old notifications
      querySnapshot.forEach(doc => {
        // Instead of deleting, we'll create an archive entry and then delete
        // This allows us to maintain a record of notifications for analytics
        const notificationData = doc.data();
        
        // Add to archived_notifications collection
        const archivedRef = doc(db, 'archived_notifications', doc.id);
        batch.set(archivedRef, {
          ...notificationData,
          archivedAt: serverTimestamp()
        });
        
        // Delete from main notifications collection
        batch.delete(doc.ref);
      });
      
      // Commit the batch
      await batch.commit();
      
      debug.log('NotificationService', `Successfully archived ${querySnapshot.size} old notifications`);
      
      return { 
        success: true, 
        count: querySnapshot.size 
      };
    } catch (error) {
      debug.error('NotificationService', 'Error cleaning up old notifications', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Send a milestone notification for booking lifecycle events
   * @param {string} bookingId - Booking ID
   * @param {string} milestone - Milestone type
   * @param {Object} data - Additional data for the notification
   * @returns {Promise<Object>} Results of notifications
   */
  async sendBookingMilestoneNotification(bookingId, milestone, data = {}) {
    try {
      // Get booking data
      const bookingRef = doc(db, 'Bookings', bookingId);
      const bookingSnap = await getDoc(bookingRef);
      
      if (!bookingSnap.exists()) {
        throw new Error(`Booking not found: ${bookingId}`);
      }
      
      const bookingData = bookingSnap.data();
      
      // Get necessary IDs
      const talentId = bookingData.talentUID;
      const organiserId = bookingData.organiserUID; 
      
      if (!talentId || !organiserId) {
        throw new Error(`Missing user IDs in booking: ${bookingId}`);
      }
      
      // Get event details
      const eventName = bookingData.event?.eventName || bookingData.Event?.eventName || 'your event';
      const eventDate = bookingData.event?.date || bookingData.Event?.date;
      const formattedDate = eventDate ? formatDate(eventDate) : null;
      
      // Prepare notification data
      const notificationData = {
        ...data,
        eventName,
        formattedDate,
        date: eventDate
      };
      
      // Handle different milestone types
      let results = { talent: null, organiser: null };
      
      switch (milestone) {
        case 'booking_created':
          // Already handled by sendBookingRequestNotifications
          break;
          
        case 'booking_confirmed':
          // Send confirmation to both parties
          results.talent = await this.sendBookingNotification(
            talentId, 
            this.USER_TYPES.TALENT, 
            'booking_confirmed', 
            bookingId, 
            notificationData
          );
          
          results.organiser = await this.sendBookingNotification(
            organiserId, 
            this.USER_TYPES.ORGANISER, 
            'booking_confirmed', 
            bookingId, 
            notificationData
          );
          break;
          
        case 'review_reminder':
          // Send review reminders to both parties
          results.talent = await this.sendReviewReminder(
            talentId,
            this.USER_TYPES.TALENT,
            bookingId,
            notificationData
          );
          
          results.organiser = await this.sendReviewReminder(
            organiserId,
            this.USER_TYPES.ORGANISER,
            bookingId,
            notificationData
          );
          break;
          
        case 'event_reminder':
          // Calculate days until event
          const daysUntilEvent = eventDate ? 
            Math.ceil((new Date(eventDate) - new Date()) / (1000 * 60 * 60 * 24)) : 
            null;
          
          if (daysUntilEvent !== null) {
            // Add days info to notification data
            const reminderData = {
              ...notificationData,
              daysAway: daysUntilEvent
            };
            
            // Send reminder to both parties
            results.talent = await this.sendBookingNotification(
              talentId,
              this.USER_TYPES.TALENT,
              'booking_reminder',
              bookingId,
              reminderData
            );
            
            results.organiser = await this.sendBookingNotification(
              organiserId,
              this.USER_TYPES.ORGANISER,
              'booking_reminder',
              bookingId,
              reminderData
            );
          }
          break;
          
        // Add more milestone types as needed
          
        default:
          debug.warn('NotificationService', `Unknown milestone type: ${milestone}`);
      }
      
      return {
        success: results.talent?.success || results.organiser?.success,
        results
      };
    } catch (error) {
      debug.error('NotificationService', 'Error sending booking milestone notification', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Expire notifications that haven't been read after a certain time
   * @param {number} daysUntilExpiration - Number of days until a notification expires
   * @returns {Promise<Object>} Result of the expiration operation
   */
  async expireOldNotifications(daysUntilExpiration = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysUntilExpiration);
      
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('createdAt', '<', Timestamp.fromDate(cutoffDate)),
        where('isRead', '==', false),
        limit(450)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        debug.log('NotificationService', 'No old notifications to expire');
        return { success: true, count: 0 };
      }
      
      debug.log('NotificationService', `Found ${querySnapshot.size} old notifications to expire`);
      
      // Create a batch for the write operations
      const batch = writeBatch(db);
      
      querySnapshot.forEach(doc => {
        batch.update(doc.ref, {
          status: this.STATUS.EXPIRED,
          expiredAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      
      // Commit the batch
      await batch.commit();
      
      debug.log('NotificationService', `Successfully expired ${querySnapshot.size} old notifications`);
      
      return { 
        success: true, 
        count: querySnapshot.size 
      };
    } catch (error) {
      debug.error('NotificationService', 'Error expiring old notifications', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Check if a user has notifications for a specific booking
   * @param {string} userId - User ID
   * @param {string} bookingId - Booking ID
   * @returns {Promise<boolean>} Whether user has notifications for the booking
   */
  async hasNotificationsForBooking(userId, bookingId) {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('bookingId', '==', bookingId),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      debug.error('NotificationService', 'Error checking notifications for booking', error);
      return false;
    }
  }
  
  /**
   * Get the appropriate notification template based on title and body patterns
   * This is for backward compatibility with older notification systems
   * @param {string} title - Notification title
   * @param {string} body - Notification body
   * @param {Object} data - Additional data to enhance the notification
   * @returns {Object|null} Matching template or null if not found
   */
  guessNotificationTemplate(title, body, data = {}) {
    const titleLower = title.toLowerCase();
    const bodyLower = body.toLowerCase();
    
    // Check for booking-related notifications
    if (titleLower.includes('booking request') || bodyLower.includes('booking request')) {
      if (titleLower.includes('received') || bodyLower.includes('received')) {
        return this.getBookingNotificationTemplate('booking_request_received', data);
      } else if (titleLower.includes('sent') || bodyLower.includes('sent')) {
        return this.getBookingNotificationTemplate('booking_request_sent', data);
      } else if (titleLower.includes('accepted') || bodyLower.includes('accepted')) {
        return this.getBookingNotificationTemplate('booking_accepted', data);
      } else if (titleLower.includes('declined') || bodyLower.includes('declined')) {
        return this.getBookingNotificationTemplate('booking_request_declined', data);
      }
    }
    
    // Check for payment-related notifications
    if (titleLower.includes('payment') || bodyLower.includes('payment')) {
      if (titleLower.includes('successful') || bodyLower.includes('successful')) {
        return this.getPaymentNotificationTemplate('payment_successful', data);
      } else if (titleLower.includes('failed') || bodyLower.includes('failed')) {
        return this.getPaymentNotificationTemplate('payment_failed', data);
      } else if (titleLower.includes('reminder') || bodyLower.includes('reminder')) {
        return this.getPaymentNotificationTemplate('payment_reminder', data);
      } else if (titleLower.includes('released') || bodyLower.includes('released')) {
        return this.getPaymentNotificationTemplate('payment_released', data);
      }
    }
    
    // Check for agreement-related notifications
    if (titleLower.includes('agreement') || bodyLower.includes('agreement')) {
      if (titleLower.includes('signed') || bodyLower.includes('signed')) {
        return this.getAgreementNotificationTemplate('agreement_signed', data);
      } else if (titleLower.includes('expired') || bodyLower.includes('expired')) {
        return this.getAgreementNotificationTemplate('agreement_expired', data);
      } else if (titleLower.includes('reminder') || bodyLower.includes('reminder')) {
        return this.getAgreementNotificationTemplate('agreement_reminder', data);
      }
    }
    
    // Default to system notification if no specific template is matched
    return {
      subject: title,
      body: body,
      priority: this.PRIORITIES.MEDIUM,
      category: this.CATEGORIES.SYSTEM
    };
  }
  
  /**
   * Create support notification for system administrators
   * @param {Object} notificationData - Notification data
   * @param {string} notificationData.type - Type of support notification
   * @param {string} notificationData.bookingId - Related booking ID
   * @param {string} notificationData.message - Notification message
   * @param {Object} notificationData.data - Additional notification data
   * @param {string} notificationData.priority - Notification priority
   * @returns {Promise<Object>} Result of notification creation
   */
  async createSupportNotification({ type, bookingId, message, data = {}, priority = 'medium' }) {
    try {
      debug.log('NotificationService', 'Creating support notification', { type, bookingId });
      
      // Create notification document in Support_Notifications collection
      const supportNotification = {
        type,
        bookingId,
        message,
        data,
        priority,
        status: 'pending',
        createdAt: serverTimestamp(),
        readAt: null,
        resolvedAt: null,
        resolvedBy: null,
        category: this.CATEGORIES.SYSTEM,
        // Additional metadata for admin tracking
        metadata: {
          source: 'automated',
          environment: process.env.NODE_ENV || 'development',
          timestamp: new Date().toISOString()
        }
      };
      
      // Add to Support_Notifications collection
      const docRef = await addDoc(collection(db, 'Support_Notifications'), supportNotification);
      
      debug.log('NotificationService', 'Support notification created', { 
        notificationId: docRef.id,
        type,
        bookingId 
      });
      
      // Also send email notification to support team if high priority
      if (priority === 'high' || priority === 'urgent') {
        try {
          const supportEmails = process.env.REACT_APP_SUPPORT_EMAILS?.split(',') || ['support@gigify.co.za'];
          
          for (const email of supportEmails) {
            await this.sendEmailNotification(
              email.trim(),
              `[${priority.toUpperCase()}] Support Alert: ${type}`,
              `
                <h3>Support Notification</h3>
                <p><strong>Type:</strong> ${type}</p>
                <p><strong>Message:</strong> ${message}</p>
                <p><strong>Booking ID:</strong> ${bookingId}</p>
                <p><strong>Priority:</strong> ${priority}</p>
                <h4>Additional Details:</h4>
                <pre>${JSON.stringify(data, null, 2)}</pre>
                <p><small>Generated at: ${new Date().toISOString()}</small></p>
              `,
              {
                category: 'support_alert',
                priority
              }
            );
          }
          
          debug.log('NotificationService', 'Support email sent for high priority notification');
        } catch (emailError) {
          debug.error('NotificationService', 'Failed to send support email', emailError);
          // Don't throw - notification was still created
        }
      }
      
      return {
        success: true,
        notificationId: docRef.id
      };
      
    } catch (error) {
      debug.error('NotificationService', 'Error creating support notification', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Send event update notifications to active talent applicants
   * @param {string} eventId - Event ID
   * @param {Object} eventData - Updated event data
   * @param {Object} changes - Changes made to the event
   * @param {Array} activeApplicants - List of talent IDs with active applications
   * @returns {Promise<Object>} Notification results
   */
  async sendEventUpdateNotifications(eventId, eventData, changes, activeApplicants) {
    try {
      debug.log('NotificationService', 'Sending event update notifications', {
        eventId,
        changesCount: Object.keys(changes).length,
        applicantCount: activeApplicants.length
      });

      if (activeApplicants.length === 0) {
        return { success: true, sent: 0, message: 'No active applicants to notify' };
      }

      // Format changes for notification
      const changesSummary = this.formatChangesForNotification(changes);
      const changesDetail = this.formatDetailedChanges(changes);

      const results = [];

      // Send notification to each active talent applicant
      for (const talentId of activeApplicants) {
        try {
          const notificationData = {
            eventId: eventId,
            eventName: eventData.aEventName || eventData.eventName || 'Event',
            changesSummary: changesSummary,
            changesDetail: changesDetail,
            changes: changes
          };

          const result = await this.sendNotification(
            talentId,
            `Event Updated: ${notificationData.eventName}`,
            `The event "${notificationData.eventName}" you applied to has been updated. ${changesSummary}`,
            {
              category: this.CATEGORIES.EVENT,
              priority: this.PRIORITIES.HIGH,
              channel: this.CHANNELS.BOTH,
              data: notificationData,
              template: 'event_updated'
            }
          );

          results.push({ talentId, success: result.success });

        } catch (error) {
          debug.error('NotificationService', `Failed to notify talent ${talentId}`, error);
          results.push({ talentId, success: false, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;

      debug.log('NotificationService', 'Event update notifications completed', {
        total: results.length,
        success: successCount,
        failed: failCount
      });

      return {
        success: true,
        sent: successCount,
        failed: failCount,
        results: results
      };

    } catch (error) {
      debug.error('NotificationService', 'Error sending event update notifications', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Format changes for notification summary
   * @param {Object} changes - Changes object
   * @returns {string} Formatted summary
   */
  formatChangesForNotification(changes) {
    const changeTypes = [];
    const keys = Object.keys(changes);

    if (keys.includes('eventDate') || keys.includes('aEventDate')) changeTypes.push('date');
    if (keys.some(k => k.includes('Time'))) changeTypes.push('time');
    if (keys.includes('eventLocation') || keys.includes('aEventLocation')) changeTypes.push('location');
    if (keys.includes('eventDescription') || keys.includes('aEventDescription')) changeTypes.push('description');
    if (keys.some(k => k.includes('budget') || k.includes('Budget'))) changeTypes.push('budget');
    if (keys.some(k => k.includes('slot') || k.includes('Slot'))) changeTypes.push('performance slots');
    if (keys.some(k => k.includes('requirement') || k.includes('Requirement'))) changeTypes.push('requirements');

    if (changeTypes.length === 0) return 'Event details have been updated';
    if (changeTypes.length === 1) return `Event ${changeTypes[0]} has been updated`;
    if (changeTypes.length === 2) return `Event ${changeTypes[0]} and ${changeTypes[1]} have been updated`;

    return `Event ${changeTypes.slice(0, -1).join(', ')}, and ${changeTypes[changeTypes.length - 1]} have been updated`;
  }

  /**
   * Format detailed changes for email notification
   * @param {Object} changes - Changes object
   * @returns {string} Formatted details
   */
  formatDetailedChanges(changes) {
    const details = [];

    for (const [key, value] of Object.entries(changes)) {
      if (key === 'eventDate' || key === 'aEventDate') {
        details.push(`• Date: ${value.new || 'Updated'}`);
      } else if (key.includes('Time')) {
        details.push(`• Time: ${value.new || 'Updated'}`);
      } else if (key === 'eventLocation' || key === 'aEventLocation') {
        details.push(`• Location: ${value.new || 'Updated'}`);
      } else if (key === 'eventDescription' || key === 'aEventDescription') {
        details.push(`• Description: Updated`);
      } else if (key.includes('budget') || key.includes('Budget')) {
        details.push(`• Budget: Updated`);
      }
    }

    return details.length > 0 ? details.join('\n') : 'Multiple event details have been updated.';
  }

  /**
   * Clear notification cache
   * @param {string} type - Type of cache to clear ('users', 'templates', or null for all)
   */
  clearCache(type = null) {
    cache.clearAll(type);
    debug.log('NotificationService', `Cache cleared: ${type || 'all'}`);
  }
}

export default new NotificationService();
